import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import {
  FormDateField,
  FormDialog,
  FormSearchSelectField,
  FormSection,
  FormTextField,
  type SearchSelectOption,
} from '@/components/common/FormDialog';
import { LOADING_PLACEHOLDER, TAKA_SYMBOL } from '@/constants/app.constants';
import { donationBudgetHoldersQueryOptions } from '@/features/users/api/users.api';
import { getPersonOptionDetail } from '@/utils/personOption';
import { donationProjectsQueryOptions } from '../api/marketPromotionP2.api';
import { BUDGET_HOLDER_LABEL } from '../constants/marketPromotion.constants';
import { useSaveDonationBudget } from '../hooks/useMpP2Mutations';
import {
  donationBudgetFormSchema,
  newDonationBudgetFormSchema,
  type DonationBudget,
  type DonationBudgetFormValues,
  type UpdateDonationBudgetPayload,
} from '../types/marketPromotionP2.types';

const NO_PICK = 0;
const PICK_HOLDER_MESSAGE = 'Pick a budget holder';

const NEW_BUDGET_VALUES: DonationBudgetFormValues = {
  userId: NO_PICK,
  promoItemId: NO_PICK,
  budgetAmount: '',
  disburseAmount: '',
  budgetDate: '',
  targetDate: '',
};

function getSavedValues(budget: DonationBudget): DonationBudgetFormValues {
  return {
    userId: budget.userId ?? NO_PICK,
    promoItemId: budget.promoItemId ?? NO_PICK,
    budgetAmount: String(budget.budgetAmount),
    disburseAmount: String(budget.disburseAmount),
    budgetDate: budget.budgetDate?.slice(0, 10) ?? '',
    targetDate: budget.targetDate?.slice(0, 10) ?? '',
  };
}

function toUpdatePayload(values: DonationBudgetFormValues): UpdateDonationBudgetPayload {
  return {
    userId: values.userId,
    promoItemId: values.promoItemId,
    disburseAmount: Number(values.disburseAmount),
    ...(values.budgetAmount === '' ? {} : { budgetAmount: Number(values.budgetAmount) }),
  };
}

interface DonationBudgetFormDialogProps {
  budget: DonationBudget | null;
  onClose: () => void;
}

export function DonationBudgetFormDialog({ budget, onClose }: DonationBudgetFormDialogProps) {
  const isEditMode = budget !== null;
  const form = useForm<DonationBudgetFormValues>({
    resolver: zodResolver(isEditMode ? donationBudgetFormSchema : newDonationBudgetFormSchema),
    defaultValues: isEditMode ? getSavedValues(budget) : NEW_BUDGET_VALUES,
  });
  const holdersQuery = useQuery(donationBudgetHoldersQueryOptions());
  const itemsQuery = useQuery(donationProjectsQueryOptions());
  const save = useSaveDonationBudget(onClose);

  const holderOptions = useMemo<SearchSelectOption[]>(
    () =>
      (holdersQuery.data ?? []).map((holder) => ({
        value: holder.id,
        label: holder.fullName,
        description: getPersonOptionDetail(holder),
        searchText: holder.username,
      })),
    [holdersQuery.data],
  );
  const itemOptions = useMemo<SearchSelectOption[]>(
    () =>
      (itemsQuery.data ?? []).map((item) => ({
        value: item.promoItemId,
        label: item.itemName,
        isActive: item.isActive,
      })),
    [itemsQuery.data],
  );

  function handleSave(values: DonationBudgetFormValues) {
    const isHolderListed = holderOptions.some((holder) => holder.value === values.userId);
    if (!isHolderListed) {
      form.setError('userId', { message: PICK_HOLDER_MESSAGE });
      return;
    }

    const payload = toUpdatePayload(values);
    if (isEditMode) {
      save.mutate({ id: budget.id, payload });
      return;
    }
    save.mutate({
      id: null,
      payload: { ...payload, budgetDate: values.budgetDate, targetDate: values.targetDate },
    });
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      icon={Wallet}
      title={isEditMode ? 'Edit Budget' : 'Add Budget'}
      description={
        isEditMode
          ? 'You can change the holder, the item and the amounts. The dates stay the same.'
          : 'Give a budget holder a budget for one cash donation item.'
      }
      form={form}
      isPending={save.isPending}
      isEditMode={isEditMode}
      onSubmit={handleSave}
      width="wide"
    >
      <FormSection>
        <FormSearchSelectField
          control={form.control}
          name="promoItemId"
          label="Donation Item"
          options={itemOptions}
          placeholder={itemsQuery.isLoading ? LOADING_PLACEHOLDER : 'Pick an item'}
          searchPlaceholder="Search items"
          emptyText="No item matches that."
          isFullRow
        />
        <FormSearchSelectField
          control={form.control}
          name="userId"
          label={BUDGET_HOLDER_LABEL}
          options={holderOptions}
          placeholder={holdersQuery.isLoading ? LOADING_PLACEHOLDER : 'Pick a budget holder'}
          searchPlaceholder="Name, HR ID, login or mobile"
          emptyText="Nobody matches that."
          isFullRow
        />
        <FormTextField
          control={form.control}
          name="budgetAmount"
          label="Budget Amount"
          placeholder="500000"
          inputMode="numeric"
          leadingSymbol={TAKA_SYMBOL}
          hasTabularNumbers
        />
        <FormDateField
          control={form.control}
          name="budgetDate"
          label="Budget Date"
          isDisabled={isEditMode}
        />
        <FormTextField
          control={form.control}
          name="disburseAmount"
          label="Disburse Amount"
          placeholder="0"
          inputMode="numeric"
          leadingSymbol={TAKA_SYMBOL}
          hasTabularNumbers
        />
        <FormDateField
          control={form.control}
          name="targetDate"
          label="Target Date"
          isDisabled={isEditMode}
        />
      </FormSection>
    </FormDialog>
  );
}
